import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { setAuthToken } from "../lib/api";
import { setSentryUser } from "../lib/sentry";

const AuthContext = createContext(null);

// 보유 포인트/프로필 헤더의 콜드 스타트 깜빡임(0→실값) 제거용 스냅샷.
// 낙관적 표시 전용 — 항상 실제 /auth/me 결과로 덮어쓰며, 세션 만료(401) 시 즉시 제거.
const LS_USER = "zm_user_snapshot_v1";
const LS_REF = "zm_ref_code"; // ?ref= 로 유입된 초대 코드(가입 후 자동 적용)

// URL의 ?ref= 를 캡처해 localStorage에 저장(가입/로그인 완료 후 자동 적용).
function captureRefFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const ref = (p.get("ref") || "").trim().toUpperCase();
    if (ref && /^[A-Z0-9]{4,16}$/.test(ref)) localStorage.setItem(LS_REF, ref);
  } catch (e) { /* noop */ }
}
function readUserSnapshot() {
  try {
    const raw = localStorage.getItem(LS_USER);
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u && typeof u.points === "number" ? { ...u, _stale: true } : null;
  } catch (e) {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const refApplied = React.useRef(false);

  // ?ref= 캡처는 마운트 즉시 1회(어떤 페이지로 유입되든).
  useEffect(() => { captureRefFromUrl(); }, []);

  // setUser 래핑 — 실제 user는 localStorage 스냅샷으로 보관, 로그아웃/만료 시 제거.
  const setUser = useCallback((u) => {
    setUserState(u);
    try {
      if (u && !u._stale && typeof u.points === "number") localStorage.setItem(LS_USER, JSON.stringify(u));
      else if (u === null) localStorage.removeItem(LS_USER);
    } catch (e) { /* noop */ }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401 is the normal unauth state; only log unexpected errors in dev.
      if (process.env.NODE_ENV !== "production" && e?.response && e.response.status !== 401) {
        console.warn("[AuthContext] /auth/me failed:", e.response.status, e.message);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  // 로그인 사용자가 아직 추천인이 없고 저장된 ?ref= 초대 코드가 있으면 1회 자동 적용(가입 직후).
  useEffect(() => {
    if (!user || user._stale || refApplied.current) return;
    if (user.referred_by) { try { localStorage.removeItem(LS_REF); } catch (e) {} return; }
    let code = "";
    try { code = localStorage.getItem(LS_REF) || ""; } catch (e) {}
    if (!code) return;
    refApplied.current = true;
    api.post("/referrals/apply", { code })
      .catch(() => { /* 본인/중복/유효하지않음/비활성 등은 조용히 무시 */ })
      .finally(() => {
        try { localStorage.removeItem(LS_REF); } catch (e) {}
        checkAuth();
      });
  }, [user, checkAuth]);

  useEffect(() => {
    // Skip Google-OAuth session check on admin routes — admin uses JWT via AdminRoute.
    if (window.location.pathname.startsWith("/admin")) {
      setLoading(false);
      return;
    }
    // Skip on Google OAuth callback page — it will set user explicitly.
    if (window.location.pathname.startsWith("/auth/google")) {
      setLoading(false);
      return;
    }
    // 낙관적 hydration: 직전 로그인 스냅샷이 있으면 즉시 표시(스피너/0 깜빡임 제거).
    // 곧바로 /auth/me로 검증·갱신하며, 만료면 setUser(null) → 로그인으로 이동.
    const snap = readUserSnapshot();
    if (snap) {
      setUserState(snap);
      setLoading(false);
    }
    checkAuth();
  }, [checkAuth]);

  // (1-a) 앱 포그라운드 복귀/창 포커스 시 /auth/me refetch — 보유 포인트 잔액을 최신화.
  // 잦은 중복 호출 방지 위해 5초 throttle. 관리자 경로는 제외(JWT 사용).
  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return;
    let last = 0;
    const onResume = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < 5000) return;
      last = now;
      checkAuth();
    };
    window.addEventListener("focus", onResume);
    document.addEventListener("visibilitychange", onResume);
    let appL = null;
    import("@capacitor/app")
      .then(({ App }) => {
        App.addListener("appStateChange", ({ isActive }) => { if (isActive) onResume(); }).then((h) => { appL = h; });
      })
      .catch(() => {});
    return () => {
      window.removeEventListener("focus", onResume);
      document.removeEventListener("visibilitychange", onResume);
      if (appL) { try { appL.remove(); } catch (e) { /* noop */ } }
    };
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[logout]", e?.message);
    }
    setAuthToken(null);
    setUser(null);
    setSentryUser(null);
    window.location.href = "/";
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, logout, refresh: checkAuth }),
    [user, loading, logout, checkAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
